import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useParams, useNavigate, useSearchParams } from 'react-router'
import { gql } from 'graphql-request'
import { useStores } from '@/components/StoreProvider'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import Layout from '@/components/Layout/Layout'
import LoadingSpinner from '@/components/Common/LoadingSpinner'
import CoachMessageCard, { parseCoachMessage } from '@/pages/Module2/CoachMessageCard'

const GET_ORGANISATION_SESSION_MESSAGES = gql`
  query getOrganisationSessionMessages($sessionToken: String!) {
    getOrganisationSessionMessages(sessionToken: $sessionToken) {
      sessionToken
      appName
      messages {
        fold_name
        messages {
          role
          content
        }
      }
      startTime
      endTime
      createdAt
    }
  }
`

// Helper component for displaying message bubbles
const MessageBubble: React.FC<{
  role: string
  content: string
}> = ({ role, content }) => {
  const isUser = role === 'user'
  if (!content) return null // Skip empty messages
  if (role !== 'user' && role !== 'assistant') return null
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4 animate-in fade-in duration-200`}>
      <div
        className={`max-w-[85%] sm:max-w-[75%] rounded-md px-4 py-3 shadow-sm transition-shadow hover:shadow-md ${
          isUser ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-gray-50 text-gray-800 border border-gray-100 rounded-bl-md'
        }`}
      >
        <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{content}</p>
      </div>
    </div>
  )
}

const ChatDetailPage: React.FC = observer(() => {
  const { chatId } = useParams<{ chatId: string }>()
  const [searchParams] = useSearchParams()
  const isAdmin = searchParams.get('admin') === 'true'
  const { chatHistoryStore } = useStores()
  const navigate = useNavigate()

  // Admin mode local state
  const [adminSession, setAdminSession] = useState<any>(null)
  const [adminLoading, setAdminLoading] = useState(false)
  const [adminError, setAdminError] = useState<string | null>(null)

  useEffect(() => {
    if (!chatId) return

    if (isAdmin) {
      // Admin mode: use dedicated admin query
      const fetchAdminSession = async () => {
        try {
          setAdminLoading(true)
          setAdminError(null)
          const response = await authorizedGraphQLRequest<{
            getOrganisationSessionMessages: any
          }>(GET_ORGANISATION_SESSION_MESSAGES, { sessionToken: chatId })
          setAdminSession(response.getOrganisationSessionMessages)
        } catch (error) {
          console.error('Failed to fetch admin session:', error)
          setAdminError('Failed to load session')
        } finally {
          setAdminLoading(false)
        }
      }
      fetchAdminSession()
    } else {
      // Normal mode: use existing store
      chatHistoryStore.getSessionByToken(chatId)
    }

    return () => {
      if (!isAdmin) {
        chatHistoryStore.currentSession = null
      }
      setAdminSession(null)
    }
  }, [chatId, isAdmin])

  const fromPage = searchParams.get('from')

  const handleBack = () => {
    if (fromPage === 'coach') {
      navigate('/coach?tab=chats')
    } else if (isAdmin) {
      const params = new URLSearchParams()
      const org = searchParams.get('org')
      if (org) params.set('org', org)
      params.set('tab', 'chats')
      navigate(`/dashboard?${params.toString()}`)
    } else {
      navigate('/history')
    }
  }

  const currentSession = isAdmin ? adminSession : chatHistoryStore.currentSession
  const isLoading = isAdmin ? adminLoading : chatHistoryStore.isLoading
  const error = isAdmin ? adminError : chatHistoryStore.error
  const coachSession =
    currentSession?.messages.find(
      (fold) =>
        fold.fold_name === 'Coach' || fold.fold_name === 'Coach Voice Collector' || fold.fold_name === 'coach_fold' || fold.fold_name === 'Role Play Eng' || fold.fold_name === 'Feedback Assistant',
    ) || currentSession?.messages.find((fold) => fold.messages?.some((msg) => msg.role === 'assistant' || msg.role === 'user'))

  return (
    <Layout>
      <div className="min-h-[calc(100vh-80px)] w-full flex flex-col bg-gradient-to-b from-slate-50 to-gray-100/80 dark:from-slate-900/50 dark:to-slate-800/30">
        <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full p-4 sm:p-6">
          {/* Header Section */}
          <div className="flex flex-wrap justify-between gap-4 mb-6 pb-4 border-b border-gray-200/80 dark:border-slate-700/50 items-center bg-white/60 dark:bg-slate-800/40 backdrop-blur-sm rounded-2xl px-4 sm:px-6 py-4 shadow-sm">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium rounded-xl px-3 py-2 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-all duration-200 active:scale-[0.98]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              {fromPage === 'coach' ? 'Back to Coach Dashboard' : isAdmin ? 'Back to Dashboard' : 'Back to History'}
            </button>

            {currentSession && (
              <div className="text-right space-y-0.5">
                <div className="text-sm font-medium text-gray-600 dark:text-slate-300">{chatHistoryStore.formatDate(currentSession.createdAt)}</div>
                <div className="text-xs text-gray-500 dark:text-slate-400 bg-gray-100 dark:bg-slate-700/50 rounded-lg px-2.5 py-1 inline-block">{currentSession.appName}</div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && <div className="mb-4 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 px-4 py-3 shadow-sm">{error}</div>}

          {/* Loading State */}
          {isLoading && (
            <div className="flex-1 flex justify-center items-center py-12 rounded-2xl bg-white/50 dark:bg-slate-800/30 backdrop-blur-sm">
              <LoadingSpinner size="lg" text="Loading session..." color="teal" />
            </div>
          )}

          {/* Session Messages */}
          {!isLoading && currentSession && (
            <div className="flex-1 overflow-y-auto rounded-2xl bg-white/70 dark:bg-slate-800/40 backdrop-blur-sm p-4 sm:p-6 shadow-sm border border-gray-100 dark:border-slate-700/50 scroll-smooth">
              <div className="space-y-1">
                {coachSession?.messages.map((message: { role: string; content: string }, messageIndex: number) => {
                  // Module 2 coach welcome messages are persisted as a JSON payload — render them as the
                  // structured coach card (wrapped so the .module2-coach-scoped CSS vars resolve) instead of
                  // dumping the raw JSON string.
                  const coachPayload = message.role === 'assistant' ? parseCoachMessage(message.content || '') : null
                  if (coachPayload) {
                    return (
                      <div key={`${messageIndex}`} className="module2-coach">
                        <CoachMessageCard payload={coachPayload} />
                      </div>
                    )
                  }
                  return <MessageBubble key={`${messageIndex}`} role={message.role} content={message.content || ''} />
                })}
              </div>
            </div>
          )}

          {/* No Session Found */}
          {!isLoading && !currentSession && !error && (
            <div className="flex-1 flex flex-col items-center justify-center py-16 px-4 rounded-2xl bg-white/60 dark:bg-slate-800/40 backdrop-blur-sm border border-gray-100 dark:border-slate-700/50">
              <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-slate-700 flex items-center justify-center mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-gray-400 dark:text-slate-500"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <p className="text-center text-gray-500 dark:text-slate-400 font-medium">Session not found</p>
              <p className="text-center text-sm text-gray-400 dark:text-slate-500 mt-1 max-w-sm">The session may have expired or you don't have permission to view it.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
})

export default ChatDetailPage
